import {produce} from 'immer';
import {omit, orderBy, uniqBy} from 'lodash';
import React, {useEffect, useMemo, useState} from 'react';
import {v4 as uuid} from 'uuid';
import {useShallow} from 'zustand/react/shallow';
import {Box, Stack, styled} from '@mui/material';
import type {IconName} from '@lightricks/react-design-system';
import {
  Button,
  designSystemToken,
  Headline,
  Icon,
} from '@lightricks/react-design-system';
import type {MenuOption} from '@contentlab/components/actions-menu/ActionsMenu';
import AssetsList from '@contentlab/components/assets-list';
import DropdownButton from '@contentlab/components/dropdown-button';
import SplitLayout from '@contentlab/components/split-layout';
import ToggleButton from '@contentlab/components/toggle-button';
import VideoDisplay from '@contentlab/components/video-display';
import {ROUTES} from '@contentlab/config/routesConstants';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useProcessEvent from '@contentlab/hooks/analytics/use-process-event';
import useViewDismissedEvent from '@contentlab/hooks/analytics/use-view-dismissed-event';
import useViewPresentedEvent from '@contentlab/hooks/analytics/use-view-presented-event';
import useLiveEstimatedMinutes from '@contentlab/hooks/use-live-estimated-minutes';
import useProjectActions from '@contentlab/hooks/use-project-actions';
import usePushNotifications from '@contentlab/hooks/use-push-notifications/usePushNotifications';
import projectsStore, {
  ProjectPageSorting,
  ProjectSortField,
  projectsStoreActions,
} from '@contentlab/stores/projectsStore';
import ProjectAction from '@contentlab/types/ProjectAction';
import {
  CompactAsset,
  FeatureName,
  Flow,
  FlowStatus,
  Project,
  ProjectStatus,
  UserInputUpscale,
} from '@contentlab/types/project';
import {AssetProps, ProjectProps} from '@contentlab/types/projectProps';
import downloadAsset from '@contentlab/utils/downloadAsset';
import {downloadAssetsProcess} from '@contentlab/utils/downloadAssets';
import {downloadFlowProcess} from '@contentlab/utils/downloadFlow';
import {FEATURE_NAME_TO_METADATA_PROPERTY} from '@contentlab/utils/featureHelpers';
import mapProjectToProjectProps from '@contentlab/utils/mapProjectToProjectProps';
import CheckedFilesSwitch from '@contentlab/views/contentlab/pages/project-page/components/checked-files-switch';
import LoadingAssetPreview from '@contentlab/views/contentlab/pages/project-page/components/loading-asset-preview';
import VideoSubtitle from '@contentlab/views/contentlab/pages/project-page/components/video-subtitle';
import {
  ASSETS_SORT_OPTION,
  FEATURE_SORT_OPTION,
} from '@contentlab/views/contentlab/pages/project-page/constants';
import {
  CONTENT_LAB_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import queryClient from '@/utils/reactQueryClient';
import translate from '@/utils/translate';
import SpinningLoader from '@/components/spinning-loader';
import {CONTENT_LAB, PROJECTS} from '@/hooks/content-studio/consts';
import useDeleteAsset from '@/hooks/content-studio/mutations/use-delete-asset';
import useRenameAsset from '@/hooks/content-studio/mutations/use-rename-asset';
import useProjectQuery from '@/hooks/content-studio/use-project-query';
import useNavigation from '@/hooks/use-navigation';
import useParams from '@/hooks/use-params';
import FinalCreditModal from '../../pricing/components/final-credit-modal/FinalCreditModal';
import DeleteAssetsModal, {
  DeleteAssetsModalReason,
} from './components/delete-assets-modal/DeleteAssetsModal';
import ProjectHeader from './components/project-header';

enum ViewOption {
  Results = 'results',
  Uploads = 'uploads',
}

const VIEW_OPTIONS = () => [
  {
    label: translate('Uploads'),
    value: ViewOption.Uploads,
  },
  {
    label: translate('Results'),
    value: ViewOption.Results,
  },
];

const Container = styled(Stack)(() => ({
  width: '100%',
  height: '100%',
  maxWidth: '1600px',
  minWidth: '1053px',
  minHeight: '711px',
  margin: '0 auto',
  flexDirection: 'column',
  gap: '24px',
}));

const DownloadSelectedAssetButton = styled(Button)(() => ({
  width: '33px',
  height: '33px',
  borderRadius: '16px',
  padding: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const VideoContainer = styled(Box)(() => ({
  'display': 'flex',
  'width': '100%',
  'height': '100%',
  'flexDirection': 'column',
  'alignItems': 'center',
  'gap': '24px',
  'flex': '1 0 0',
  'alignSelf': 'stretch',
  'borderRadius': '32px',
  'background': designSystemToken(`semantic.bg.secondary`),
  'boxSizing': 'border-box',
  '*': {
    outline: 'none',
  },
}));

const AssetsContainer = styled(Stack)(() => ({
  boxSizing: 'border-box',
  display: 'flex',
  padding: '24px 24px 0 24px',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '24px',
  flex: '1 0 0',
  alignSelf: 'center',
  borderRadius: '32px',
  border: `1px solid ${designSystemToken('semantic.bg.neutral-secondary')}`,
  background: designSystemToken(`semantic.bg.secondary`),
}));

const AssetsControlsContainer = styled(Stack)(() => ({
  boxSizing: 'border-box',
  display: 'flex',
  width: '100%',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '16px',
}));

const ListActionsContainer = styled(Stack)(() => ({
  height: '24px',
  flexDirection: 'row',
  width: '100%',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

function getSortOptions(viewOption: ViewOption, feature: FeatureName) {
  if (feature === FeatureName.REPHRASING) {
    return [];
  }
  const baseSortOptions =
    viewOption === ViewOption.Results ? [ASSETS_SORT_OPTION.STATUS] : [];
  return [...baseSortOptions, ...FEATURE_SORT_OPTION[feature]];
}

function mergeFlowAsset(
  feature: FeatureName,
  flow: Flow,
  asset: CompactAsset,
  type: 'inputAsset' | 'outputAsset'
): AssetProps | null {
  if (!asset) {
    return null;
  }
  const isInputAsset = type === 'inputAsset';

  return {
    compositeId: isInputAsset ? asset.id : `${flow.id}-${asset.id}`,
    ...omit(flow, 'id', 'inputAsset', 'outputAsset'),
    ...asset,
    feature,
    flowId: isInputAsset ? asset.id : flow.id,
    status: isInputAsset ? null : flow.status,
    type: isInputAsset ? 'inputAsset' : 'outputAsset',
    pairedAssetId: !isInputAsset ? flow.inputAsset.id : undefined,
  } as AssetProps;
}

const SORT_PATH = {
  [ProjectSortField.STATUS]: 'status',
  [ProjectSortField.FILE_NAME]: 'name',
  [ProjectSortField.LANGUAGE]: 'metadata.language',
  [ProjectSortField.ASPECT_RATIO]: 'metadata.aspectRatio',
  [ProjectSortField.RESOLUTION]: 'metadata.resolution',
};

function sortedAssets(
  assets: AssetProps[],
  projectSorting: ProjectPageSorting | null
) {
  if (!projectSorting) {
    return assets;
  }

  if (projectSorting.field === ProjectSortField.ASPECT_RATIO) {
    return orderBy(
      assets,
      [
        (asset) => Number(asset.metadata?.aspectRatio?.split(':')[0]) || 0,
        (asset) => Number(asset.metadata?.aspectRatio?.split(':')[1]) || 0,
      ],
      [projectSorting.direction]
    );
  }

  if (projectSorting.field === ProjectSortField.RESOLUTION) {
    return orderBy(
      assets,
      [
        (asset) => {
          // For non-ready output assets, use target resolution from userInput
          if (
            asset.type === 'outputAsset' &&
            asset.status !== 'ready' &&
            asset.userInput
          ) {
            const upscaleInput = asset.userInput as UserInputUpscale;
            return upscaleInput?.outputResolution || 0;
          }

          // For ready assets and input assets, use actual dimensions
          if (asset.metadata?.width && asset.metadata?.height) {
            return Math.min(asset.metadata.width, asset.metadata.height);
          }

          return 0;
        },
      ],
      [projectSorting.direction]
    );
  }

  return orderBy(
    assets,
    [SORT_PATH[projectSorting.field]],
    [projectSorting.direction]
  );
}

function useProjectAssets(
  project: ProjectProps | null | undefined,
  type: 'inputAsset' | 'outputAsset',
  projectSorting: ProjectPageSorting | null
) {
  return useMemo(() => {
    if (!project) {
      return [];
    }
    const assets = project.flows
      .flatMap((flow) => {
        const asset = flow[type];
        return mergeFlowAsset(
          project.feature,
          flow,
          asset || flow.inputAsset,
          type
        );
      })
      .filter(Boolean) as AssetProps[];
    if (type === 'inputAsset') {
      return sortedAssets(
        uniqBy(assets, (asset) => asset.id),
        projectSorting
      );
    }
    return sortedAssets(assets, projectSorting);
  }, [project, type, projectSorting]);
}

const syncProjectCacheAfterAssetRename = (
  projectId: string,
  {assetId, name}: {assetId: string; name: string}
) => {
  queryClient.setQueryData(
    [CONTENT_LAB, PROJECTS, projectId],
    (old: Project | undefined) => {
      if (!old) {
        return undefined;
      }
      return produce(old, (draft: Project) => {
        draft.flows.forEach((flow) => {
          if (flow.flow.inputAsset.id === assetId) {
            flow.flow.inputAsset.name = name;
          }
          if (flow.flow?.outputAsset?.id === assetId) {
            flow.flow.outputAsset.name = name;
          }
        });
      });
    }
  );
};

const syncProjectCacheAfterAssetDelete = (
  projectId: string,
  assetIds: string[]
) => {
  queryClient.setQueryData(
    [CONTENT_LAB, PROJECTS, projectId],
    (old: Project | undefined) => {
      if (!old) {
        return undefined;
      }
      return produce(old, (draft: Project) => {
        // mark isArchived as true for the assets that are deleted
        draft.flows.forEach((flow) => {
          if (assetIds.includes(flow.flow.inputAsset.id)) {
            flow.flow.inputAsset.isArchived = true;
          }
          if (
            flow.flow.outputAsset &&
            assetIds.includes(flow.flow.outputAsset.id)
          ) {
            flow.flow.outputAsset.isArchived = true;
          }
        });
      });
    }
  );
};

function LoadingProject() {
  return (
    <Stack
      sx={{
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '32px',
      }}
    >
      <Headline size="lg" color={designSystemToken('semantic.fg.secondary')}>
        {translate('Loading project')}
      </Headline>
      <Box sx={{transform: 'scale(2)'}}>
        <SpinningLoader
          color={designSystemToken('semantic.fg.secondary')}
          containerHeight={28}
        />
      </Box>
    </Stack>
  );
}

function ProjectPage() {
  const {projectId} = useParams();
  const {data: project, refetch} = useProjectQuery({id: projectId});
  const [selectedCompositeId, setSelectedCompositeId] = useState<string | null>(
    null
  );
  const [isVideoPlaying, setIsVideoPlaying] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const [checkedAssetIds, setCheckedAssetIds] = useState<string[]>([]);
  const [filesCheckedOn, setFilesCheckedOn] = useState<boolean>(false);
  const [selectedViewOption, setSelectedViewOption] = useState<ViewOption>(
    ViewOption.Results
  );
  const isUploadsTabSelected = selectedViewOption === ViewOption.Uploads;
  const [downloadInProgress, setDownloadInProgress] = useState(false);
  const {uiInteraction} = useUiInteractionEvent();
  const {started, ended} = useProcessEvent();
  const {viewPresented} = useViewPresentedEvent();
  const {viewDismissed} = useViewDismissedEvent();
  const {sendNotification} = usePushNotifications();

  const handleDownloadAsset = async (asset: AssetProps) => {
    if (!asset?.flowId || !projectData?.id) {
      return;
    }
    const interactionId = uuid();
    await downloadFlowProcess(
      projectData.id,
      asset.flowId,
      interactionId,
      CONTENT_LAB_VIEW_NAMES.PROJECT,
      {started, ended}
    );
  };

  const [deleteAssetModal, setDeleteAssetModal] = useState<AssetProps | null>(
    null
  );
  const [deleteAssetModalPresentationId, setDeleteAssetModalPresentationId] =
    useState(uuid());

  const {navigate} = useNavigation();

  const {onProjectActionClick, projectActionModals} = useProjectActions({
    interaction_location: CONTENT_LAB_VIEW_NAMES.PROJECT,
    flow_name: FLOW_NAMES.CONTENT_LAB.PROJECT,
    onActionComplete: (action: ProjectAction) => {
      switch (action) {
        case ProjectAction.Rename:
          refetch();
          break;
        case ProjectAction.Delete:
          navigate(`${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_PROJECTS}`);
          break;
        case ProjectAction.Download:
          break;
        default:
          throw new Error(`Unknown project action: ${action}`);
      }
    },
  });

  const {projectSorting} = projectsStore(
    useShallow((state) => ({
      projectSorting: state.projectPageSorting[projectId],
    }))
  );

  const showStatusOnCompletion =
    localStorage.getItem(`showReady_${projectId}`) === 'true';

  const notifyProjectReady =
    localStorage.getItem(`notifyProjectReady_${projectId}`) === 'true';

  useEffect(() => {
    projectsStoreActions.setProjectPageDefaultSorting(projectId);
    if (project?.status === ProjectStatus.READY && notifyProjectReady) {
      localStorage.removeItem(`notifyProjectReady_${projectId}`);
      sendNotification(
        CONTENT_LAB_VIEW_NAMES.PROJECT as keyof typeof CONTENT_LAB_VIEW_NAMES,
        'Project Ready',
        'Your project is ready!',
        () => {
          window.open(
            `${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_PROJECT}/${projectId}`,
            '_blank'
          );
        }
      );
    }
    return () => {
      if (
        project?.status &&
        [ProjectStatus.READY, ProjectStatus.FAILED].includes(project.status)
      ) {
        localStorage.removeItem(`showReady_${projectId}`);
      }
    };
  }, [showStatusOnCompletion, project?.status, projectId]);

  const {mutateAsync: renameAsset} = useRenameAsset({
    onMutate: ({assetId, name}) => {
      syncProjectCacheAfterAssetRename(project?.id || '', {
        assetId,
        name,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CONTENT_LAB, PROJECTS, projectData?.id]);
    },
  });
  const {mutateAsync: deleteAssets} = useDeleteAsset({
    onMutate: (assetIds: string[]) => {
      syncProjectCacheAfterAssetDelete(project?.id || '', assetIds);
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CONTENT_LAB, PROJECTS, projectData?.id]);
    },
  });

  const projectData = useMemo(() => {
    if (!project) {
      return null;
    }
    return mapProjectToProjectProps(project);
  }, [project]);

  const uploadAssets = useProjectAssets(
    projectData,
    'inputAsset',
    projectSorting
  );
  const resultAssets = useProjectAssets(
    projectData,
    'outputAsset',
    projectSorting
  );

  const selectedAssetsByView = useMemo(() => {
    if (selectedViewOption === ViewOption.Results) {
      return resultAssets;
    }
    return uploadAssets;
  }, [selectedViewOption, uploadAssets, resultAssets]);

  const nonArchivedAssets = useMemo(() => {
    return selectedAssetsByView.filter((asset) => !asset.isArchived);
  }, [selectedAssetsByView]);

  const hasMultipleFiles = useMemo(() => {
    return nonArchivedAssets.length > 1;
  }, [nonArchivedAssets]);

  // Reset multi-file selection state when file count drops to 1 or below
  // This prevents stale state when files are deleted or view changes
  useEffect(() => {
    if (!hasMultipleFiles && filesCheckedOn) {
      setFilesCheckedOn(false);
      setCheckedAssetIds([]);
    }
  }, [hasMultipleFiles, filesCheckedOn, setFilesCheckedOn, setCheckedAssetIds]);

  const selectedAsset = useMemo(() => {
    const sources = [...uploadAssets, ...resultAssets];
    return sources.find((asset) => asset.compositeId === selectedCompositeId);
  }, [selectedCompositeId, uploadAssets, resultAssets]);

  const pairedInputAsset = useMemo(() => {
    if (!selectedAsset || selectedAsset.type !== 'outputAsset') {
      return null;
    }
    if (selectedAsset.status === 'ready') {
      return null; // Output is ready, don't need input
    }
    return (
      uploadAssets.find((asset) => asset.id === selectedAsset.pairedAssetId) ||
      null
    );
  }, [selectedAsset, uploadAssets]);

  const isVideoLoading = useMemo(() => {
    return (
      selectedAsset?.type === 'outputAsset' &&
      selectedAsset?.status !== 'ready' &&
      !!pairedInputAsset
    );
  }, [selectedAsset, pairedInputAsset]);

  const getVideoUrl = useMemo(() => {
    if (
      selectedAsset?.status === 'ready' ||
      selectedAsset?.type === 'inputAsset'
    ) {
      return selectedAsset?.url || '';
    }
    return pairedInputAsset?.url || '';
  }, [selectedAsset, pairedInputAsset]);

  const {progressPercentage, hasExceededEstimate} = useLiveEstimatedMinutes(
    selectedAsset?.estimatedCompletionTime,
    selectedAsset?.createdAt
  );

  const currentFlowId = useMemo(() => {
    if (checkedAssetIds.length === 0) return undefined;

    const firstCheckedAsset = selectedAssetsByView.find((asset) =>
      checkedAssetIds.includes(asset.id)
    );
    return firstCheckedAsset?.flowId;
  }, [checkedAssetIds, selectedAssetsByView]);

  const handleAssetClick = (flowAsset: AssetProps) => {
    uiInteraction({
      item_name: 'asset_clicked',
      interaction_location: CONTENT_LAB_VIEW_NAMES.PROJECT,
      flow_name: FLOW_NAMES.CONTENT_LAB.PROJECT,
      asset_id: flowAsset.id,
    });
    setSelectedCompositeId(flowAsset.compositeId);
    setIsPlaying(true);
  };

  const handleCheckAsset = (flowAsset: AssetProps, selected: boolean) => {
    uiInteraction({
      item_name: selected ? 'asset_checked' : 'asset_unchecked',
      interaction_location: CONTENT_LAB_VIEW_NAMES.PROJECT,
      flow_name: FLOW_NAMES.CONTENT_LAB.PROJECT,
      asset_id: flowAsset.id,
    });
    if (checkedAssetIds.includes(flowAsset.id)) {
      setCheckedAssetIds((prev) => prev.filter((id) => id !== flowAsset.id));
    } else {
      setCheckedAssetIds((prev) => [...prev, flowAsset.id]);
    }
  };

  useEffect(() => {
    const assets =
      selectedViewOption === ViewOption.Results ? resultAssets : uploadAssets;

    if (assets.length === 0) return;

    const hasSelectedAsset = assets.some(
      (asset) => asset.compositeId === selectedCompositeId && !asset.isArchived
    );
    if (hasSelectedAsset) return;

    const nonArchivedSelectableAssets = assets.filter(
      (asset) => !asset.isArchived && asset.status !== FlowStatus.FAILED
    );
    if (nonArchivedSelectableAssets.length === 0) {
      setSelectedCompositeId(null);
      return;
    }

    const [firstAsset] = nonArchivedSelectableAssets;
    if (!firstAsset) return;

    const isUpload = firstAsset.type === 'inputAsset';
    const isValidResult = [FlowStatus.READY, FlowStatus.IN_PROGRESS].includes(
      firstAsset.status
    );

    if (isUpload || isValidResult) {
      setSelectedCompositeId(firstAsset.compositeId);
      setIsPlaying(false);
    }
  }, [uploadAssets, resultAssets, selectedCompositeId, selectedViewOption]);

  // Clear selection state when switching between tabs
  useEffect(() => {
    setCheckedAssetIds([]);
    setFilesCheckedOn(false);
  }, [selectedViewOption]);

  const videoProps = useMemo(() => {
    const baseProps = {
      videoId: `${selectedAsset?.flowId}-${selectedAsset?.id}` || '',
      videoName: selectedAsset?.name || '',
      videoSubtitle:
        selectedAsset && projectData ? (
          <VideoSubtitle
            asset={selectedAsset}
            feature={projectData.feature}
            uploadAssets={uploadAssets}
            onSubtitleActionClick={(originalAsset: AssetProps) => {
              setSelectedViewOption(ViewOption.Uploads);
              setSelectedCompositeId(originalAsset.compositeId);
              uiInteraction({
                item_name: 'subtitle_action_clicked',
                asset_id: selectedAsset.id,
                interaction_location: CONTENT_LAB_VIEW_NAMES.PROJECT,
                flow_name: FLOW_NAMES.CONTENT_LAB.PROJECT,
              });
            }}
          />
        ) : null,
      videoUrl: getVideoUrl,
      isOutputLoading: isVideoLoading,
      subtitleTime: 0,
      onPlayToggle: (playing: boolean) => {
        setIsVideoPlaying(playing);
        uiInteraction({
          item_name: playing ? 'play_asset' : 'pause_asset',
          interaction_location: CONTENT_LAB_VIEW_NAMES.PROJECT,
          flow_name: FLOW_NAMES.CONTENT_LAB.PROJECT,
          asset_id: selectedAsset?.id || '',
        });
      },
      isPlayingState: {isPlaying, setIsPlaying},
    };

    if (isVideoLoading) {
      return {
        ...baseProps,
        progressPercentage,
        hasExceededEstimate,
      };
    }

    return baseProps;
  }, [
    selectedAsset,
    projectData,
    uploadAssets,
    isPlaying,
    setIsPlaying,
    pairedInputAsset,
    isVideoLoading,
    progressPercentage,
    hasExceededEstimate,
  ]);

  const isDownloadButtonDisabled = useMemo(() => {
    return (
      downloadInProgress ||
      (selectedAsset?.type === 'outputAsset' &&
        (selectedAsset?.status !== FlowStatus.READY || !selectedAsset?.url))
    );
  }, [downloadInProgress, selectedAsset]);

  const shouldHideDownloadButton = useMemo(() => {
    return (
      selectedAsset?.type === 'outputAsset' &&
      selectedAsset?.status !== FlowStatus.READY &&
      !selectedAsset?.url &&
      !isVideoLoading
    );
  }, [selectedAsset, isVideoLoading]);

  const downloadSelectedAssetButton = useMemo(() => {
    if (shouldHideDownloadButton) {
      return null;
    }
    return (
      <DownloadSelectedAssetButton
        appearance="neutral"
        size="medium"
        mode="tinted"
        isLoading={downloadInProgress}
        disabled={isDownloadButtonDisabled}
        onClick={(event) => {
          const interactionId = uuid();
          uiInteraction({
            item_name: 'download_asset',
            interaction_location: CONTENT_LAB_VIEW_NAMES.PROJECT,
            flow_name: FLOW_NAMES.CONTENT_LAB.PROJECT,
            asset_id: selectedAsset?.id || '',
            interaction_id: interactionId,
          });
          event.stopPropagation();
          if (selectedAsset) {
            downloadSingleAsset(selectedAsset, interactionId);
          }
        }}
        sx={{
          '&:disabled': {
            backgroundColor: designSystemToken(
              'semantic.bg.disabled.secondary'
            ),
          },
        }}
      >
        <Icon
          size="medium"
          appearance="neutral"
          name="Navigation-Download"
          color={
            isDownloadButtonDisabled
              ? designSystemToken('semantic.fg.disabled')
              : designSystemToken('semantic.fg.neutral')
          }
          style={{
            width: '20px',
            height: '20px',
          }}
        />
      </DownloadSelectedAssetButton>
    );
  }, [
    downloadInProgress,
    selectedAsset,
    isDownloadButtonDisabled,
    shouldHideDownloadButton,
  ]);

  const downloadSingleAsset = async (
    assetToDownload: AssetProps,
    interactionId: string
  ) => {
    if (!assetToDownload?.url) {
      return;
    }
    setDownloadInProgress(true);
    const processID = uuid();
    started({
      endpoint: assetToDownload.url,
      process_id: processID,
      process_name: 'download',
      original_id: assetToDownload.pairedAssetId,
      interaction_id: interactionId,
      asset_id: assetToDownload.id,
      source: CONTENT_LAB_VIEW_NAMES.PROJECT,
    });
    let result = '';
    let errorMessage = '';
    try {
      await downloadAsset(assetToDownload.url, assetToDownload.name || '');
      result = 'success';
      errorMessage = '';
    } catch (err) {
      result = 'error';
      errorMessage = err instanceof Error ? err.message : String(err);
    } finally {
      setDownloadInProgress(false);
      ended({
        endpoint: assetToDownload.url,
        outcome_id: assetToDownload.id,
        original_id: assetToDownload.pairedAssetId,
        process_id: processID,
        process_name: 'download',
        error: errorMessage,
        result,
      });
    }
  };

  const sortByElement = useMemo(() => {
    const sourceAssets =
      selectedViewOption === ViewOption.Uploads ? uploadAssets : resultAssets;
    if (sourceAssets.length <= 1 || !projectData) {
      return null;
    }

    const sortOptions = getSortOptions(
      selectedViewOption,
      projectData?.feature
    );

    // Build menu options for ActionsMenu
    const menuOptions: MenuOption[] = sortOptions.map((option) => ({
      id: option.id,
      title: translate(option.labelLocaleKey),
      icon: option.icon as IconName,
      onClick: () => {
        uiInteraction({
          item_name: `${option.id}_sort_by_pressed`,
          interaction_location: CONTENT_LAB_VIEW_NAMES.PROJECT,
          flow_name: FLOW_NAMES.CONTENT_LAB.PROJECT,
        });
        projectsStoreActions.setProjectPageSorting(projectData.id, {
          field: option.field,
          direction: option.direction as 'asc' | 'desc',
        });
      },
      isDisabled:
        projectSorting?.field === option.field &&
        projectSorting?.direction === option.direction,
    }));

    // Find the current sort option for button label/icon
    const currentSort =
      sortOptions.find(
        (option) =>
          projectSorting?.field === option.field &&
          projectSorting?.direction === option.direction
      ) || sortOptions[0];

    return (
      <DropdownButton
        label={`${translate('Sort by')}: ${translate(
          currentSort.labelLocaleKey
        )}`}
        menuOptions={menuOptions}
        selectedOptionId={currentSort.id}
      />
    );
  }, [
    selectedViewOption,
    uploadAssets,
    resultAssets,
    projectData,
    projectSorting,
  ]);

  const checkedFilesSwitchElement = useMemo(() => {
    if (!hasMultipleFiles || !projectData) {
      return null;
    }

    return (
      <CheckedFilesSwitch
        checkedAssetIds={checkedAssetIds}
        setCheckedAssetIds={setCheckedAssetIds}
        filesCheckedOn={filesCheckedOn}
        setFilesCheckedOn={setFilesCheckedOn}
        deleteAssets={async (assetIds) => {
          await deleteAssets(assetIds);
        }}
        downloadAssets={async (assetIds) => {
          const interactionId = uuid();

          uiInteraction({
            item_name: 'download_checked_assets',
            interaction_location: CONTENT_LAB_VIEW_NAMES.PROJECT,
            flow_name: FLOW_NAMES.CONTENT_LAB.PROJECT,
            asset_id: assetIds.join(','),
          });

          if (currentFlowId) {
            await downloadFlowProcess(
              projectData.id,
              currentFlowId,
              interactionId,
              CONTENT_LAB_VIEW_NAMES.PROJECT,
              {started, ended}
            );
          } else {
            await downloadAssetsProcess(
              assetIds,
              interactionId,
              CONTENT_LAB_VIEW_NAMES.PROJECT,
              {started, ended}
            );
          }
        }}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasMultipleFiles,
    projectData,
    checkedAssetIds,
    setCheckedAssetIds,
    filesCheckedOn,
    setFilesCheckedOn,
    deleteAssets,
    currentFlowId,
    uiInteraction,
  ]);

  if (!projectData) {
    return <LoadingProject />;
  }

  const handleDeleteModalClose = async (reason: DeleteAssetsModalReason) => {
    const interactionId = uuid();
    uiInteraction({
      item_name: reason,
      interaction_location: CONTENT_LAB_VIEW_NAMES.DELETE_ASSETS_MODAL,
      flow_name: FLOW_NAMES.CONTENT_LAB.PROJECT,
      interaction_id: interactionId,
    });
    viewDismissed({
      interaction_id: interactionId,
      presentation_id: deleteAssetModalPresentationId,
      reason: reason === DeleteAssetsModalReason.Delete ? 'confirm' : 'cancel',
    });
    if (reason === DeleteAssetsModalReason.Delete) {
      await deleteAssets([deleteAssetModal?.id || '']);
      setCheckedAssetIds(
        checkedAssetIds.filter((id) => id !== deleteAssetModal?.id)
      );
    }
    setDeleteAssetModalPresentationId(uuid());
    setDeleteAssetModal(null);
  };

  const leftColumn = (
    <AssetsContainer>
      <AssetsControlsContainer>
        <ToggleButton
          options={VIEW_OPTIONS()}
          value={selectedViewOption}
          onChange={(option) => {
            uiInteraction({
              item_name: `${option}_tab_pressed`,
              interaction_location: CONTENT_LAB_VIEW_NAMES.PROJECT,
              flow_name: FLOW_NAMES.CONTENT_LAB.PROJECT,
            });
            setSelectedViewOption(option as ViewOption);
            const sortOptions = getSortOptions(
              option as ViewOption,
              projectData?.feature
            );
            if (
              sortOptions.length &&
              !sortOptions.find(
                (sortOption) => projectSorting?.field === sortOption.field
              )
            ) {
              projectsStoreActions.setProjectPageSorting(projectData.id, {
                field: sortOptions[0].field,
                direction: sortOptions[0].direction as 'asc' | 'desc',
              });
            }
          }}
        />
        <ListActionsContainer>
          {checkedFilesSwitchElement}
          {sortByElement}
        </ListActionsContainer>
      </AssetsControlsContainer>
      <AssetsList
        isCheckPermission={showStatusOnCompletion}
        showCheckbox={filesCheckedOn}
        displayThumbnailIcon={!filesCheckedOn}
        listId={`${projectData.id}-${selectedViewOption}`}
        assets={selectedAssetsByView}
        selectedAssetId={selectedCompositeId}
        playingAssetId={isVideoPlaying ? selectedCompositeId : null}
        checkedAssetIds={checkedAssetIds}
        onCheckAsset={handleCheckAsset}
        onAssetClick={filesCheckedOn ? undefined : handleAssetClick}
        metadataDisplayParam={
          FEATURE_NAME_TO_METADATA_PROPERTY[projectData.feature]
        }
        downloadAsset={isUploadsTabSelected ? undefined : handleDownloadAsset}
        renameAsset={async ({assetId, name}) => {
          uiInteraction({
            item_name: 'rename_asset',
            interaction_location: CONTENT_LAB_VIEW_NAMES.PROJECT,
            flow_name: FLOW_NAMES.CONTENT_LAB.PROJECT,
            asset_id: assetId,
          });
          await renameAsset({assetId, name});
        }}
        deleteAsset={async (asset) => {
          const interactionId = uuid();
          uiInteraction({
            item_name: 'delete_asset',
            interaction_location: CONTENT_LAB_VIEW_NAMES.PROJECT,
            flow_name: FLOW_NAMES.CONTENT_LAB.PROJECT,
            asset_id: asset.id,
          });
          viewPresented({
            interaction_id: interactionId,
            view_name: CONTENT_LAB_VIEW_NAMES.DELETE_ASSETS_MODAL,
            presentation_id: deleteAssetModalPresentationId,
            view_type: 'popup',
            view_location: CONTENT_LAB_VIEW_NAMES.PROJECT,
            source: 'item_pressed',
          });
          setDeleteAssetModal(asset);
        }}
        onActionsClick={(assetId) => {
          uiInteraction({
            item_name: 'actions_menu_pressed',
            interaction_location: CONTENT_LAB_VIEW_NAMES.PROJECT,
            flow_name: FLOW_NAMES.CONTENT_LAB.PROJECT,
            asset_id: assetId,
          });
        }}
      />
      <DeleteAssetsModal
        title={translate('Delete video?')}
        description={translate('x_will_be_permanently_removed.', {
          assetName: deleteAssetModal?.name || '',
        })}
        open={!!deleteAssetModal}
        onClose={handleDeleteModalClose}
      />
    </AssetsContainer>
  );

  const videoPreview = (
    <VideoContainer
      sx={{
        border: videoProps.videoUrl
          ? 'unset'
          : `1px solid ${designSystemToken('semantic.bg.neutral-secondary')}`,
        paddingBottom: videoProps.videoUrl ? 'unset' : '48px',
      }}
    >
      {videoProps.videoUrl ? (
        <VideoDisplay
          key={videoProps.videoId}
          {...videoProps}
          loadingType="shimmer"
          actionComponent={
            isUploadsTabSelected ? undefined : downloadSelectedAssetButton
          }
        />
      ) : (
        <Stack
          direction="column"
          gap="24px"
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: '32px',
            backgroundColor: designSystemToken(`semantic.bg.secondary`),
            position: 'relative',
            paddingBlock: '24px',
            boxSizing: 'border-box',
          }}
        >
          <Headline
            size="md"
            color={designSystemToken('semantic.fg.inverse')}
            paddingBlockEnd="2px"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            paddingInline="24px"
          >
            {videoProps.videoName}
          </Headline>
          {selectedAsset && <LoadingAssetPreview asset={selectedAsset} />}
        </Stack>
      )}
    </VideoContainer>
  );

  return (
    <Container>
      <FinalCreditModal />
      <ProjectHeader
        project={projectData}
        shouldShowProgress={
          (project?.status !== ProjectStatus.READY &&
            project?.status !== ProjectStatus.FAILED) ||
          showStatusOnCompletion
        }
        onRenameClick={(projectProps) => {
          onProjectActionClick(ProjectAction.Rename, projectProps);
        }}
        onDeleteClick={(projectProps) => {
          onProjectActionClick(ProjectAction.Delete, projectProps);
        }}
        onMoreActionsClick={() =>
          uiInteraction({
            item_name: 'project_more_actions',
            interaction_location: CONTENT_LAB_VIEW_NAMES.PROJECT,
            flow_name: FLOW_NAMES.CONTENT_LAB.PROJECT,
            asset_id: projectData.id,
          })
        }
      />
      <SplitLayout leftColumn={leftColumn} rightColumn={videoPreview} />

      {projectActionModals}
    </Container>
  );
}

export default ProjectPage;
